import type { DocumentReference, Provenance } from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Icon, Modal, Segment, Message, Loader, Confirm, Dimmer } from 'semantic-ui-react';
import useSWR from 'swr';

import { useAuthContext } from '../../../../contexts/AuthContext';
import type { RejectionWithMessage, RejectionWithResponse } from '../../types';
import {
  fetchMemberDocumentMetadata,
  fetchMemberDocumentWithAttachment,
} from '../documentReferenceQueries';

import type { MemberDocument } from './types';
import UploadDocumentModal from './uploadDocumentModal';
import { useMemberDocumentDelete } from './useMemberDocumentDelete';
import { useMemberDocumentUpload } from './useMemberDocumentUpload';
import { filterMemberDocuments, getMemberDocumentAttachment } from './utils';



export interface MemberDocumentsProps {
  /** Patient ID for FHIR DocumentReference queries. */
  patientId?: string;
  /** Managing org ID (e.g. payor) for routing to the correct FHIR store. */
  org?: string;
  /** Callback to refresh DocumentReference data (e.g. after upload). */
  refresh?: () => void;
}

/**
 * Member Documents tab content.
 * Displays a searchable table of member documents with upload capability.
 */
function MemberDocuments({ patientId, org, refresh }: MemberDocumentsProps) {
  const authInfo = useAuthContext();
  const { uploadDocument, isUploading, error, clearError } = useMemberDocumentUpload();
  const { deleteDocument, isDeleting, error: deleteError, clearError: clearDeleteError } = useMemberDocumentDelete();

  const [searchQuery, setSearchQuery] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [downloadSuccessOpen, setDownloadSuccessOpen] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<MemberDocument | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);

  const {
    data,
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- the value is a `fhir-sdoh` export whose module ships no usable types */
    error: fetchError,
    mutate,
    isValidating,
  } = useSWR(
    patientId && authInfo?.token ? ['member-documents', patientId, authInfo.token] : null,
    () => fetchMemberDocumentMetadata(authInfo.token, patientId!),
    { revalidateOnFocus: false }
  );

  const isRefetching = Boolean(data && isValidating);

  const entries = useMemo(() => (data?.entry ?? []) as Array<{ resource: DocumentReference }>, [data?.entry]);

  const documents = useMemo(() => filterMemberDocuments(entries), [entries]);

  const refreshDocuments = useMemo(
    () => () => {
       
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
      mutate?.();
      refresh?.();
    },
    [mutate, refresh]
  );

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(
      (doc) => doc.subject.toLowerCase().includes(query) || doc.fileName.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  const handleUpload = async (subject: string, file: File) => {
    if (!patientId || !authInfo?.token) return;
    await uploadDocument({
      patientId,
      org,
      token: authInfo.token,
      file,
      subject,
      uploadedBy: authInfo.practitionerName,
    });
    refreshDocuments();
  };

  const getSafeFileName = (fileName: string, contentType?: string) => {
    if (!fileName?.trim()) {
      if (contentType === 'application/pdf') return 'document.pdf';
      if (contentType?.startsWith('image/png')) return 'document.png';
      if (contentType?.startsWith('image/jpeg')) return 'document.jpg';
      return 'document';
    }
    return fileName;
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadClick = async (doc: MemberDocument) => {
    if (!patientId) {
      return;
    }

    try {
      setDownloadError('');
      setDownloadingDocumentId(doc.id);

      let attachmentData = doc.attachmentData;
      let contentType = doc.contentType;
      let fileName = doc.fileName;

      if (!attachmentData) {
        const resource = await fetchMemberDocumentWithAttachment(authInfo.token, doc.id, patientId);
        const attachment = getMemberDocumentAttachment(resource);
        if (!attachment) {
          throw new Error('No file data found for this document.');
        }
        attachmentData = attachment.data;
        contentType = attachment.contentType;
        fileName = attachment.fileName;
      }

      const byteCharacters = atob(attachmentData);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: contentType || 'application/octet-stream',
      });

      const safeFileName = getSafeFileName(fileName, contentType);
      downloadBlob(blob, safeFileName);
      logDocumentDownload(doc, `Member document ${safeFileName} - ${doc.id} has been downloaded.`);
      setDownloadSuccessOpen(true);
      // Cast at the read rather than on the catch variable — see the same shape in
      // `tasksNotesList.tsx`.
    } catch (err: unknown) {
      console.error(err);
      setDownloadError((err as RejectionWithMessage)?.message || 'Failed to download document.');
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const logDocumentDownload = (doc: MemberDocument, message: string) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    const provenance: Provenance = ProvenanceBuilder.build(
      `Patient/${patientId}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
      patientId,
      'Member Document Download',
      message,
      { code: org!.toUpperCase(), display: org!.toLowerCase() }
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

    fhirProxyClient.send('post', '', fhirBundle).then(
      () => {
        console.log('Member document download provenance logged');
      },
      (error: { toString(): string }) => {
        console.log('Unable to create provenance: ' + error.toString());
      }
    );
  };

  const handleDeleteClick = (doc: MemberDocument) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete || !authInfo?.token || !patientId) {
      return;
    }

    await deleteDocument({
      token: authInfo.token,
      patientId,
      documentId: documentToDelete.id,
      org,
      practitionerName: authInfo.practitionerName,
    });
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
    refreshDocuments();
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
    clearDeleteError();
  };

  if (!patientId) {
    return (
      <Segment>
        <Message info>Select a patient to view member documents.</Message>
      </Segment>
    );
  }

  const isLoading = !data && !fetchError;

  return (
    <Segment>
      <Dimmer active={isRefetching} inverted>
        <Loader>Refreshing documents...</Loader>
      </Dimmer>
      {isLoading && <Loader active inline="centered" />}

      {fetchError && (
        <Message negative>
          <Message.Header>Failed to load documents</Message.Header>
          <p>
            {(fetchError as RejectionWithResponse | undefined)?.response?.data?.errorMessage ||
              (fetchError as RejectionWithResponse | undefined)?.message ||
              'Unable to load member documents.'}
          </p>
        </Message>
      )}

      {downloadError && (
        <Message negative onDismiss={() => setDownloadError('')}>
          <Message.Header>Failed to download document</Message.Header>
          <p>{downloadError}</p>
        </Message>
      )}

      {deleteError && (
        <Message negative onDismiss={clearDeleteError}>
          <Message.Header>Failed to delete document</Message.Header>
          <p>{deleteError}</p>
        </Message>
      )}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          icon="search"
          placeholder="Search by subject"
          value={searchQuery}
          onChange={(_, { value }) => setSearchQuery(value)}
          style={{ flex: 1, minWidth: '200px' }}
        />
        <Button color="blue" icon="upload" content="Upload File" onClick={() => setUploadModalOpen(true)} />
      </div>

      <div className="table-div">
        <Table basic sortable celled singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Subject</Table.HeaderCell>
              <Table.HeaderCell>File Name</Table.HeaderCell>
              <Table.HeaderCell>Last Updated</Table.HeaderCell>
              <Table.HeaderCell>Uploaded By</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isLoading &&
              filteredDocuments.map((doc) => (
                <Table.Row key={doc.id}>
                  <Table.Cell>{doc.subject}</Table.Cell>
                  <Table.Cell>{doc.fileName}</Table.Cell>
                  <Table.Cell>{doc.lastUpdated}</Table.Cell>
                  <Table.Cell>{doc.uploadedBy}</Table.Cell>
                  <Table.Cell>
                    <Button
                      icon
                      size="mini"
                      color="green"
                      onClick={() => handleDownloadClick(doc)}
                      loading={downloadingDocumentId === doc.id}
                      disabled={downloadingDocumentId === doc.id}
                      title="Download document"
                    >
                      <Icon name="download" />
                    </Button>

                    <Button icon size="mini" color="red" onClick={() => handleDeleteClick(doc)} title="Delete document">
                      <Icon name="trash" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUpload}
        isUploading={isUploading}
        error={error}
        onClearError={clearError}
      />

      <Modal open={downloadSuccessOpen} onClose={() => setDownloadSuccessOpen(false)} size="mini">
        <Modal.Content>Member document downloaded successfully.</Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={() => setDownloadSuccessOpen(false)}>
            <Icon name="checkmark" /> OK
          </Button>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={deleteConfirmOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        content={`Are you sure you want to delete "${documentToDelete?.fileName ?? documentToDelete?.subject}"? This will mark the document as invalid.`}
        confirmButton={<Button content="Delete" negative loading={isDeleting} disabled={isDeleting} />}
        cancelButton="Cancel"
        size="small"
      />
    </Segment>
  );
}

export default MemberDocuments;
export type { MemberDocument };
